// Skyfall beam tails (owner 2026-11-02: 'extend the tails of the skyfall shard to be more like beams leaving the hand, allow the
// beams to have flexible tails that curve the way they are directed and they winding like snakes'). Each shard drags a thin
// energy ribbon back along its own up-and-over arc toward the hand that threw it; the ribbon follows the curve (it is built
// from points on the path), tapers to nothing at the far end, and snakes from side to side with a wave that runs down the
// tail — removed at the owner's request: tails are now smooth, straight-following contrails. Camera-facing strip, hard white core + yellow edge, a little over 1 so it blooms.
// One pooled dynamic mesh for every tail; no per-frame allocations.
import * as THREE from 'three/webgpu';
import { abs, attribute, color, float, mix, smoothstep, uv } from 'three/tsl';

export const TAIL_PTS = 22;
const MAX_TAILS = 40;
const VERTS = MAX_TAILS * TAIL_PTS * 2;

export class SkyTrails {
  readonly mesh: THREE.Mesh;
  private geo = new THREE.BufferGeometry();
  private pos = new Float32Array(VERTS * 3);
  private uvs = new Float32Array(VERTS * 2);
  private n = 0;
  private time = 0;

  constructor(scene: THREE.Scene, edge = 0xffd84a) {
    const idx = new Uint32Array(MAX_TAILS * (TAIL_PTS - 1) * 6);
    let o = 0;
    for (let t = 0; t < MAX_TAILS; t++) for (let i = 0; i < TAIL_PTS - 1; i++) {
      const a = (t * TAIL_PTS + i) * 2;
      idx.set([a, a + 1, a + 2, a + 2, a + 1, a + 3], o); o += 6;
    }
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('uv', new THREE.BufferAttribute(this.uvs, 2).setUsage(THREE.DynamicDrawUsage));
    this.geo.setIndex(new THREE.BufferAttribute(idx, 1));
    this.geo.setDrawRange(0, 0);
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const across = abs(uv().y.sub(0.5)).mul(2), along = uv().x;          // along: 0 at the head → 1 at the tail end
    const core = smoothstep(float(0.5), float(0.3), across);
    m.colorNode = mix(color(edge), color(0xffffff), core).mul(float(1.7).sub(along.mul(0.6)));
    m.opacityNode = smoothstep(float(1.0), float(0.8), across).mul(float(1).sub(along).pow(0.55));
    void attribute;
    this.mesh = new THREE.Mesh(this.geo, m); this.mesh.frustumCulled = false; this.mesh.renderOrder = 4;
    scene.add(this.mesh);
  }

  begin(dt: number): void { this.time += dt; this.n = 0; }

  /** pts = TAIL_PTS points (x,y,z), head first, along the path; width = ribbon width at the head (m); seed varies the wave */
  add(pts: Float32Array, width: number, seed: number, cam: THREE.Vector3): void {
    if (this.n >= MAX_TAILS) return;
    const base = this.n * TAIL_PTS * 2, P = this.pos, U = this.uvs;
    for (let i = 0; i < TAIL_PTS; i++) {
      const j = i * 3, a = Math.max(0, i - 1) * 3, b = Math.min(TAIL_PTS - 1, i + 1) * 3;
      let tx = pts[b] - pts[a], ty = pts[b + 1] - pts[a + 1], tz = pts[b + 2] - pts[a + 2];
      const tl = Math.hypot(tx, ty, tz) || 1; tx /= tl; ty /= tl; tz /= tl;
      const vx = cam.x - pts[j], vy = cam.y - pts[j + 1], vz = cam.z - pts[j + 2];
      let sx = ty * vz - tz * vy, sy = tz * vx - tx * vz, sz = tx * vy - ty * vx;
      const sl = Math.hypot(sx, sy, sz) || 1; sx /= sl; sy /= sl; sz /= sl;
      const f = i / (TAIL_PTS - 1);
      // snake: a wave running down the tail, growing toward the far end (the head stays on its path)
      // owner 2026-11-02 (later): 'straight tails, not wavy' — the tail follows the arc smoothly, no side-to-side snake
      const wig = 0 * seed;
      const cx = pts[j] + sx * wig, cy = pts[j + 1] + sy * wig, cz = pts[j + 2] + sz * wig;
      const w = width * 0.5 * Math.pow(1 - f, 0.7) + 0.01;
      const v = (base + i * 2) * 3;
      P[v] = cx - sx * w; P[v + 1] = cy - sy * w; P[v + 2] = cz - sz * w;
      P[v + 3] = cx + sx * w; P[v + 4] = cy + sy * w; P[v + 5] = cz + sz * w;
      const uvi = (base + i * 2) * 2;
      U[uvi] = f; U[uvi + 1] = 0; U[uvi + 2] = f; U[uvi + 3] = 1;
    }
    this.n++;
  }

  end(): void {
    this.geo.setDrawRange(0, this.n * (TAIL_PTS - 1) * 6);
    (this.geo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.geo.attributes.uv as THREE.BufferAttribute).needsUpdate = true;
  }
  clear(): void { this.n = 0; this.geo.setDrawRange(0, 0); }
}
